import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Button, Tab, Tabs, Spinner } from "react-bootstrap";
import AOS from "aos";
import "./Advertisement.css";
import { FaTag, FaPercent, FaTrash, FaUser, FaLock, FaCalculator } from "react-icons/fa";
import HeroSection from "../heroSection/HeroSection";
import Footer from "../footer/Footer";
import { useTranslation } from "react-i18next";
import SEO from "../SEO";
import { driverAPI } from "../../api/apiSystem";
import NavbarMenu from "../heroSection/NavbarMenu";
import { MdOutlineAttachMoney, MdOutlineDiscount, MdOutlineDone } from "react-icons/md";
import { AiOutlineSafety } from "react-icons/ai";
import { IoTimeOutline } from "react-icons/io5";
import IMG_HERO_BG from "../../assets/images/assa.jpg";
import { LuBadgePercent, LuHandshake } from "react-icons/lu";
import { FiArrowUpRight } from "react-icons/fi";
import { PiWarningCircleBold } from "react-icons/pi";
import Swal from "sweetalert2";
import AppDownloadSection from "../heroSection/AppDownloadSection";

export default function Advertisement({ showNavbar = true }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [advertisements, setAdvertisements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [filteredAds, setFilteredAds] = useState([]);

  const trustBadges = [
    {
      icon: <MdOutlineDiscount />,
      label1: t("advertisement.hero.trust.free.label1"),
      label2: t("advertisement.hero.trust.free.label2")
    },
    {
      icon: <AiOutlineSafety />,
      label1: t("advertisement.hero.trust.secure.label1"),
      label2: t("advertisement.hero.trust.secure.label2")
    },
    {
      icon: <LuBadgePercent />,
      label1: t("advertisement.hero.trust.fast.label1"),
      label2: t("advertisement.hero.trust.fast.label2")
    },
  ];

  // جلب جميع الإعلانات
  const fetchAdvertisements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await driverAPI.advertisements.index();
      
      if (response.success) {
        const ads = response.data.data || [];
        setAdvertisements(ads);
        setFilteredAds(ads);
      } else {
        throw new Error(response.message || "Failed to load advertisements");
      }
    } catch (error) {
      console.error("Error fetching advertisements:", error);
      Swal.fire({
        icon: "error",
        title: t("advertisement.error"),
        text: error.message || t("advertisement.load_error"),
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  // جلب الفئات
  const fetchCategories = useCallback(async () => {
    try {
      const response = await driverAPI.advertisements.categories();
      if (response.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  // جلب الإعلانات حسب الفئة
  const fetchAdsByCategory = useCallback(async (categoryId) => {
    try {
      setLoading(true);
      let response;
      
      if (categoryId === "all" || categoryId === null || categoryId === undefined) {
        response = await driverAPI.advertisements.index();
      } else {
        response = await driverAPI.advertisements.byCategory(categoryId);
      }
      
      if (response.success) {
        const ads = response.data.data || [];
        setFilteredAds(ads);
      } else {
        throw new Error(response.message || "Failed to load advertisements");
      }
    } catch (error) {
      console.error("Error fetching category ads:", error);
      Swal.fire({
        icon: "error",
        title: t("advertisement.error"),
        text: error.message || t("advertisement.load_error"),
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    fetchAdvertisements();
    fetchCategories();
  }, [fetchAdvertisements, fetchCategories]);

  // معالج تغيير التاب
  const handleTabSelect = useCallback((tabKey) => {
    setActiveTab(tabKey);
    
    if (tabKey === "all") {
      fetchAdsByCategory("all");
    } else {
      // العثور على الفئة المحددة حسب الـ slug أو id
      const selectedCategory = categories.find(cat => {
        const catSlug = cat.slug || cat.name?.toLowerCase();
        return catSlug === tabKey || String(cat.id) === tabKey;
      });
      
      if (selectedCategory) {
        fetchAdsByCategory(selectedCategory.id);
      } else {
        fetchAdsByCategory("all");
      }
    }
  }, [categories, fetchAdsByCategory]);

  // بناء التابس من الفئات القادمة من الـ API
  const buildTabs = useCallback(() => {
    const tabs = [
      {
        eventKey: "all",
        title: t("advertisement.tabs.all"),
        categoryId: null,
      },
    ];

    categories.forEach((category) => {
      // استخدام slug أو name أو id كـ eventKey
      const eventKey = category.slug || category.name?.toLowerCase() || `category-${category.id}`;
      // استخدام name المعروف أو الترجمة
      const title = category.name || t(`advertisement.tabs.${category.slug}`) || category.slug;
      
      tabs.push({
        eventKey: eventKey,
        title: title,
        categoryId: category.id,
        slug: category.slug,
      });
    });

    return tabs;
  }, [categories, t]);

  // دالة لتحويل النص إلى مصفوفة من العناصر (للعرض مع علامة صح)
  const parseDescription = (description) => {
    if (!description) return [];
    // تقسيم النص على الفواصل أو النقاط
    const items = description.split(',').map(item => item.trim()).filter(item => item);
    return items;
  };

  return (
    <>
      <SEO
        title="TransCirox | Angebote und Werbung"
        description="Profitieren Sie von den besten Angeboten und Rabatten auf Transport- und Versanddienstleistungen."
        keywords="Alle Angebote, Reifen & Service, TankKarten, Versicherung, Fahrzeugzubehor, Verpackungsmaterial, Leasing & Finanzierung"
      />

      {showNavbar && <NavbarMenu />}

      {/* Hero Section */}
      <section
        className="umzug-hero"
        style={{
          backgroundImage: `linear-gradient(to right, rgb(27 27 27 / 97%) 45%, rgb(51 51 51 / 55%) 70%, transparent 100%), url(${IMG_HERO_BG})`,
        }}
        aria-label="Hero"
      >
        <Container>
          <div className="hero-content" data-aos="fade-right">
            <h6>
              {t("advertisement.hero.subtitle1")} {`>`}{" "}
              <span className="text-bold">{t("advertisement.hero.subtitle2")}</span>
            </h6>

            <h1>{t("advertisement.hero.title")}</h1>
            <h2>
              & <span className="text-orange">{t("advertisement.hero.tagline")}</span>
            </h2>

            <p className="hero-subtitle">{t("advertisement.hero.des")}</p>

            <div className="trust-badges mt-4">
              {trustBadges.map(({ icon, label1, label2 }, i) => (
                <div className="trust-badge" key={i}>
                  <div className="badge-icon" aria-hidden="true">
                    {icon}
                  </div>
                  <div>
                    <div dangerouslySetInnerHTML={{ __html: label1 }} />
                    <div>{label2}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Categories Tabs Section */}
      <section className="py-5 bg-light categories-advertisement">
        <Tabs
          activeKey={activeTab}
          onSelect={handleTabSelect}
          id="driver-all-tabs"
          className="custom-tabs px-5 mb-4 fw-bold justify-content-center dark-tabs"
          fill
        >
          {buildTabs().map((tab) => (
            <Tab
              key={tab.eventKey}
              eventKey={tab.eventKey}
              title={<span>{t(`advertisement.tabs.${tab.title}`)}</span>}
            >
              <Container>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="warning" />
                    <p className="mt-3 text-muted">{t("driverRequest.general.loading")}</p>
                  </div>
                ) : filteredAds.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted fs-5">{t("advertisement.no_ads")}</p>
                  </div>
                ) : (
                  <Row className="g-4 pt-4">
                    {filteredAds.map((ad, index) => {
                      // تحويل الوصف إلى مصفوفة عناصر
                      const descriptionItems = parseDescription(ad.description);
                      
                      return (
                        <Col lg={6} xl={4} key={ad.id} data-aos="fade-up" data-aos-delay={index * 100}>
                          <Card className="h-100 shadow-lg border-0 overflow-hidden promotion-card">
                            {/* صورة الإعلان */}
                            <div className="position-relative">
                              <Card.Img
                                variant="top"
                                src={ad.image}
                                alt={ad.title}
                                className="promotion-img"
                                style={{
                                  height: "200px",
                                  objectFit: "cover",
                                  width: "100%",
                                }}
                                onError={(e) => {
                                  e.target.src = "https://via.placeholder.com/400x200?text=No+Image";
                                }}
                              />
                              {ad.category_name && (
                                <span className="badge bg-orange position-absolute top-0 end-0 m-3">
                                  {ad.category_name}
                                </span>
                              )}
                            </div>

                            <div className="d-flex flex-column p-3">
                              <Card.Title className="fw-bold fs-4 mb-2 text-orange">
                                {ad.title}
                              </Card.Title>
                              
                              {/* عرض الوصف كقائمة مع علامة صح */}
                              <div className="description-list mb-3">
                                {descriptionItems.length > 0 ? (
                                  descriptionItems.map((item, idx) => (
                                    <div key={idx} className="description-item d-flex align-items-start">
                                      <MdOutlineDone className="text-success me-2 flex-shrink-0 mt-1" />
                                      <span className="text-muted">{item}</span>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-muted">{ad.description}</p>
                                )}
                              </div>

                              <div className="mt-auto">
                                {/* زر الرابط */}
                                {ad.url ? (
                                  <a
                                    href={ad.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-orange w-100 d-flex align-items-center justify-content-center gap-2"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {t("advertisement.promotion.btn")}
                                    <FiArrowUpRight />
                                  </a>
                                ) : (
                                  <Button
                                    variant="orange"
                                    size="lg"
                                    className="mt-auto d-flex align-items-center justify-content-center gap-2 w-100"
                                    disabled
                                  >
                                    {t("advertisement.promotion.btn")}
                                    <FiArrowUpRight />
                                  </Button>
                                )}

                                <p className="text-muted text-center pt-2 promotion-text">
                                  {t("advertisement.promotion.text")} <PiWarningCircleBold />
                                </p>
                              </div>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Container>
            </Tab>
          ))}
        </Tabs>
      </section>

      {/* Warning Section */}
      <section className="py-4">
        <Container>
          <Card className="h-100 border-0 shadow-sm p-4 benefit-card" data-aos="fade-up">
            <Container>
              <Row className="align-items-center gy-4">
                <Col xs="auto">
                  <div className="icon-why-circle" aria-hidden="true">
                    <PiWarningCircleBold />
                  </div>
                </Col>
                <Col lg={10} className="warning-text">
                  <p>
                    {t("advertisement.warning.text1")}
                    <br />
                    {t("advertisement.warning.text2")}
                  </p>
                </Col>
              </Row>
            </Container>
          </Card>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="pt-4">
        <Container>
          <section className="cta-section" id="angebot" data-aos="fade-up">
            <Container>
              <Row className="align-items-center gy-4">
                <Col xs="auto">
                  <div className="cta-icon-wrap" aria-hidden="true">
                    <LuHandshake />
                  </div>
                </Col>
                <Col className="cta-text">
                  <h3>{t("advertisement.cta.title")}</h3>
                  <p>{t("advertisement.cta.text")}</p>
                </Col>
                <Col xs={12} sm="auto" className="cta-action text-center">
                  <a href="/contact" className="btn-orange">
                    {t("advertisement.cta.btn")}
                  </a>
                </Col>
              </Row>
            </Container>
          </section>
        </Container>
      </section>

      <AppDownloadSection />

      <Footer />
    </>
  );
}